import { useEffect, useState } from "react";
import { api, tokens, type SessionState, type User } from "../api";
import { useBatch } from "../batch";
import { comprimirFoto, tamanhoKb } from "../comprimirFoto";
import { ResultPill } from "../components";
import { ScanScreen } from "../ScanScreen";
import { aplicarTema, OPCOES, temaAtual } from "../tema";

export function Historico() {
  const [session, setSession] = useState<SessionState | null>(null);

  useEffect(() => {
    void api.session().then(setSession);
  }, []);

  return (
    <main>
      <h2 className="section">Suas últimas leituras</h2>

      {!session || session.scans.length === 0 ? (
        <p className="empty">Nenhuma leitura ainda.</p>
      ) : (
        <div className="batch">
          {session.scans.map((s) => (
            <div key={s.id} className="batch-item">
              <div className="top">
                <code>{s.package.code}</code>
                <ResultPill result={s.result} />
              </div>
              <div className="who">
                {s.account ? `${s.account.platform.name} · ${s.account.name}` : "sem conta"} ·{" "}
                {new Date(s.createdAt).toLocaleTimeString("pt-BR", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="note">
        Aqui aparecem só as leituras da sessão aberta. O histórico completo, com
        filtros por conta, transportadora e data, fica no site administrativo.
      </p>
    </main>
  );
}

/// Teto por devolução, igual ao do servidor. Oito ângulos cobrem qualquer
/// avaria; mais que isso costuma ser toque preso no botão.
const MAX_FOTOS = 8;

const MOTIVOS = [
  // O mais comum de longe: cliente reclamando que o pedido ainda não chegou.
  "Não entregue",
  "Recusado pelo cliente",
  "Endereço não encontrado",
  "Cliente ausente",
  "Avaria no transporte",
  "Extravio revertido",
  // "Outro" abre um campo livre: a lista fixa nunca cobre tudo, e forçar um
  // motivo errado é pior para o relatório do que um texto do operador.
  "Outro",
];

export function Devolucao() {
  const [reason, setReason] = useState<string | null>(null);
  const [outro, setOutro] = useState("");
  // Etiquetas rápidas que se somam ao motivo, em vez de substituí-lo — dá pra
  // marcar "Não entregue" E "Urgência" ao mesmo tempo, sem digitar nada.
  const [urgente, setUrgente] = useState(false);
  /// Vazio = sem horário-limite marcado.
  const [horaEntrega, setHoraEntrega] = useState("");
  const [fotos, setFotos] = useState<string[]>([]);
  const [valor, setValor] = useState("");
  const [cameraOpen, setCameraOpen] = useState(false);
  const batch = useBatch();

  /// O que já foi entregue nesta sessão. Guardado aqui na tela, e não vindo do
  /// servidor, porque o valor está em mostrar ao operador o que ELE acabou de
  /// fazer — para conferir contra a pilha de pacotes na bancada.
  const [historico, setHistorico] = useState<
    Array<{ code: string; motivo: string; custoCents: number | null; fotos: number }>
  >([]);

  // O motivo que vai valer na próxima leitura. Trocá-lo NÃO mexe no que já foi
  // bipado: cada item guarda o motivo do instante em que entrou no lote.
  //
  // As etiquetas rápidas (Urgência, Entregar até) entram junto no mesmo
  // texto — o campo que guarda o motivo é um só, e criar uma coluna nova só
  // pra isso duplicaria informação sem precisar.
  const motivoBase = reason === "Outro" ? outro.trim() || null : reason;
  const extras = [urgente && "Urgente", horaEntrega && `Entregar até ${horaEntrega}`].filter(
    (v): v is string => !!v
  );
  const motivoAtual = motivoBase && extras.length > 0 ? `${motivoBase} — ${extras.join(" · ")}` : motivoBase;

  /// Centavos a partir do texto, nunca por Number(v) * 100: em ponto flutuante
  /// 45.50 * 100 dá 4549.999… e truncar perderia um centavo por devolução.
  const valorEmCentavos = (() => {
    const limpo = valor.trim().replace(",", ".");
    if (!/^\d+(\.\d{1,2})?$/.test(limpo)) return null;
    const [reais = "0", cents = ""] = limpo.split(".");
    return Number(reais) * 100 + Number(cents.padEnd(2, "0"));
  })();

  const devolucoes = batch.items.filter((i) => i.direction === "RETURN");

  return (
    <>
      <main>
        <h2 className="section">Devolução</h2>
        <p className="note">
          Fluxo separado da expedição. O que é bipado aqui <b>não</b> conta como pacote
          despachado nem entra no valor pago à transportadora.
        </p>

        <h2 className="section">1. Motivo da devolução</h2>
        <div className="choices">
          {MOTIVOS.map((m) => (
            <button
              key={m}
              onClick={() => setReason(m)}
              style={
                reason === m
                  ? {
                      background: "var(--dep-lime)",
                      borderColor: "var(--dep-lime)",
                      color: "var(--dep-black)",
                    }
                  : undefined
              }
            >
              {m}
            </button>
          ))}
        </div>

        {reason === "Outro" && (
          <input
            autoFocus
            className="reason-input"
            placeholder="Descreva o motivo"
            maxLength={200}
            value={outro}
            onChange={(e) => setOutro(e.target.value)}
          />
        )}

        {reason && (
          <>
            <h2 className="section">Detalhes rápidos (opcional)</h2>
            <div className="choices">
              <button
                onClick={() => setUrgente((v) => !v)}
                style={
                  urgente
                    ? { background: "var(--dep-lime)", borderColor: "var(--dep-lime)", color: "var(--dep-black)" }
                    : undefined
                }
              >
                Urgência
              </button>
              <button
                onClick={() => setHoraEntrega((h) => (h ? "" : "12:00"))}
                style={
                  horaEntrega
                    ? { background: "var(--dep-lime)", borderColor: "var(--dep-lime)", color: "var(--dep-black)" }
                    : undefined
                }
              >
                Entregar até horário
              </button>
              {horaEntrega && (
                <input
                  type="time"
                  className="reason-input"
                  style={{ maxWidth: 120 }}
                  value={horaEntrega}
                  onChange={(e) => setHoraEntrega(e.target.value)}
                />
              )}
            </div>
          </>
        )}

        {devolucoes.length > 0 && (
          <p className="note">
            Trocar o motivo agora vale só para os <b>próximos</b> pacotes. Os {devolucoes.length}{" "}
            já bipados mantêm o motivo com que foram registrados.
          </p>
        )}

        <h2 className="section">2. Desconto por dano (opcional)</h2>
        <input
          className="reason-input"
          inputMode="decimal"
          placeholder="0,00"
          value={valor}
          onChange={(e) => setValor(e.target.value)}
        />
        <p className="note">
          Quando o pacote volta danificado, este valor é <b>descontado</b> da fatura da
          transportadora no fechamento. Deixe vazio se não houve dano.
        </p>

        <h2 className="section">
          3. Fotos (opcional){fotos.length > 0 && ` · ${fotos.length} de ${MAX_FOTOS}`}
        </h2>

        {fotos.length > 0 && (
          <div className="fotos-grade">
            {fotos.map((f, i) => (
              <div key={i} className="foto-item">
                <img src={f} alt={`Foto ${i + 1} da devolução`} />
                <button
                  className="remover"
                  aria-label={`Remover foto ${i + 1}`}
                  onClick={() => setFotos((prev) => prev.filter((_, j) => j !== i))}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        {fotos.length < MAX_FOTOS && (
          <label className="foto-campo">
            <span>
              {fotos.length === 0
                ? "Tocar para fotografar o pacote"
                : "Tocar para adicionar outro ângulo"}
            </span>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              // `multiple` deixa escolher várias da galeria de uma vez; com a
              // câmera, o aparelho ignora e tira uma por vez, como esperado.
              multiple
              hidden
              onChange={(e) => {
                const arquivos = [...(e.target.files ?? [])];
                if (arquivos.length === 0) return;

                const cabem = arquivos.slice(0, MAX_FOTOS - fotos.length);

                // Comprime antes de guardar: as fotos sobem junto com o
                // Entregar, e 8 MB cada em rede de galpão faria o operador
                // esperar.
                void Promise.all(cabem.map(comprimirFoto))
                  .then((novas) => setFotos((prev) => [...prev, ...novas]))
                  .catch(() => undefined);

                // Zera o input: sem isso, escolher o mesmo arquivo de novo não
                // dispara o evento e parece que o app travou.
                e.target.value = "";
              }}
            />
          </label>
        )}

        {fotos.length > 0 && (
          <p className="note">
            Cerca de {fotos.reduce((s, f) => s + tamanhoKb(f), 0)} KB no total. São anexadas
            aos pacotes bipados a partir de agora, e sobem junto com o Entregar.
          </p>
        )}

        <h2 className="section">4. Bipar os pacotes</h2>
        <div className="actions">
          <button
            className="btn primary"
            disabled={!motivoAtual}
            onClick={() => setCameraOpen(true)}
          >
            {motivoAtual ? "Abrir câmera" : "Escolha o motivo primeiro"}
          </button>
        </div>

        {devolucoes.length > 0 && (
          <>
            <h2 className="section">No lote · {devolucoes.length}</h2>
            <div className="batch">
              {devolucoes.map((i) => (
                <div key={i.key} className="batch-item">
                  <div className="top">
                    <code>{i.check.code}</code>
                  </div>
                  {/* O motivo vem do ITEM, não do estado da tela — é isso que
                      impede trocar o motivo e reescrever o histórico. */}
                  <div className="who">
                    {i.accountName ?? "sem conta"} · {i.reason ?? "sem motivo"}
                    {i.returnCostCents !== null &&
                      ` · R$ ${(i.returnCostCents / 100).toFixed(2).replace(".", ",")}`}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Histórico do que já foi entregue nesta sessão. Fica embaixo, depois
            do que ainda está em conferência: o operador olha para cima para
            trabalhar e para baixo para conferir o que já passou. */}
        {historico.length > 0 && (
          <>
            <h2 className="section">Entregues nesta sessão · {historico.length}</h2>
            <div className="batch">
              {historico.map((h) => (
                <div key={h.code} className="batch-item">
                  <div className="top">
                    <code>{h.code}</code>
                    <span className="pill ok">registrado</span>
                  </div>
                  <div className="who">
                    {h.motivo}
                    {h.custoCents !== null &&
                      ` · R$ ${(h.custoCents / 100).toFixed(2).replace(".", ",")}`}
                    {h.fotos > 0 && ` · ${h.fotos} foto(s)`}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </main>

      {cameraOpen && (
        <ScanScreen
          direction="RETURN"
          reason={motivoAtual}
          fotos={fotos}
          valorCents={valorEmCentavos}
          titulo={`Devolução — ${motivoAtual}`}
          onClose={() => setCameraOpen(false)}
          onEntregue={(entregues) => {
            // Passa para o histórico e limpa o formulário. Sem isso, o motivo e
            // a foto do lote anterior valeriam para a próxima devolução sem
            // ninguém perceber — e a prova de um pacote iria parar em outro.
            setHistorico((prev) => [...entregues, ...prev]);
            setReason(null);
            setOutro("");
            setUrgente(false);
            setHoraEntrega("");
            setFotos([]);
            setValor("");
          }}
        />
      )}
    </>
  );
}

export function Perfil({ user, onLogout }: { user: User; onLogout: () => void }) {
  const [tema, setTema] = useState(temaAtual());

  return (
    <main>
      <h2 className="section">Operador</h2>
      <div className="batch-item">
        <div className="top">
          <b>{user.name}</b>
          <span className="pill ok">{user.role === "ADMIN" ? "Administrador" : "Operador"}</span>
        </div>
        <div className="who">{user.email}</div>
      </div>

      <h2 className="section">Aparência</h2>
      <div className="choices">
        {OPCOES.map((o) => (
          <button
            key={o.valor}
            onClick={() => {
              aplicarTema(o.valor);
              setTema(o.valor);
            }}
            style={
              tema === o.valor
                ? {
                    background: "var(--dep-lime)",
                    borderColor: "var(--dep-lime)",
                    color: "var(--dep-black)",
                  }
                : undefined
            }
          >
            <span aria-hidden="true" style={{ marginRight: 6 }}>
              {o.icone}
            </span>
            {o.rotulo}
          </button>
        ))}
      </div>
      <p className="note">
        <b>Automático</b> segue o tema do celular. No galpão vale escolher à mão: com luz
        baixa a tela clara ofusca, e com sol na bancada a escura some.
      </p>

      {/* O botão de zerar contadores saiu daqui.
          Os contadores da tela de Bipar não são mais de SESSÃO: seguem a
          coleta aberta, e zeram sozinhos quando alguém finaliza a coleta. Um
          botão que diz "zerar contadores" e não zera contador nenhum é pior
          que botão nenhum — ensina a duvidar dos outros.

          Fechar a sessão continua existindo no servidor, e é o que separa uma
          lista de histórico da seguinte. Nada na tela dependia disso. */}

      <h2 className="section">Acesso</h2>
      <div className="actions">
        <button
          className="btn"
          onClick={() => {
            tokens.clear();
            onLogout();
          }}
        >
          Sair
        </button>
      </div>
      <p className="note">
        Configurações, financeiro e relatórios ficam no site administrativo, no
        computador. Este aplicativo é só bipagem.
      </p>
    </main>
  );
}
